import { useCallback, useEffect, useState } from 'react';

import { RubricEditorModal } from './rubric-editor-modal';
import { DomainEditorModal } from './domain-editor-modal';
import { WidgetEditorModal } from './widget-editor-modal';
import { ParseTreeModal } from './parse-tree-modal';
import { EvidenceGraphModal, type EvidenceGraphFocal } from './evidence-graph-modal';
import { CreateFormModal, type CreateKind } from './create-form-modal';
import { CriterionHistoryModal } from './criterion-history-modal';
import { EmailTemplateModal } from './email-template-modal';
import { SearchIndexModal } from './search-index-modal';
import { NewEngagementWizard } from './new-engagement-wizard';
import { OverrideHostBridge } from './override-host-bridge';
import type { EvidenceSourceType } from '@/lib/api/evidence-links';

/**
 * T-0114 / E10 — Global editor-modal host. Extended by T-0116 to also
 * host the misc modals (evidence graph + create + crit-history + email
 * templates + search index).
 *
 * The prototype wires each `data-open-*` attribute via a global click
 * delegate (`shell.js → bindAll`); in production we mirror that by
 * mounting a single host once at the root layout that subscribes to
 * delegated clicks. Each `data-open-*` attribute opens a modal seeded
 * with the value it carries:
 *
 *   - data-open-rubric="<engagement_id>"      → RubricEditorModal
 *   - data-open-domains="<engagement_id>"     → DomainEditorModal
 *   - data-open-widgets="<framework_def_id>"  → WidgetEditorModal
 *   - data-open-parse="<document_id>"         → ParseTreeModal
 *   - data-open-evidence="<source_type>:<source_id>"
 *                                              → EvidenceGraphModal
 *   - data-create="<kind>"                     → CreateFormModal
 *   - data-open-crit-history="<score_id>"      → CriterionHistoryModal
 *   - data-open-email-templates                → EmailTemplateModal
 *     (or data-open-email-templates="<event_type>" to focus a row)
 *   - data-open-index                          → SearchIndexModal
 *     (engagement id sniffed from the URL)
 *
 * Why a host instead of per-screen modal mount points:
 *   - Triggers fire from many screens (Go-Live overview, scorecard,
 *     criteria editor, health domains, firm
 *     settings library, document viewer). Mounting modals inline
 *     duplicates state and breaks the round-trip semantics.
 *   - A single root-level host lets every consumer just stamp the
 *     `data-open-*` attribute on any button or link without importing
 *     the modal component locally.
 *
 * Event delegation:
 *   - We attach one capture-phase `click` listener on `document`. When
 *     the click target (or any ancestor) carries a recognized attribute,
 *     we read its value, set the active modal state, and stop the click
 *     from propagating further. Capture phase keeps the host ordering
 *     deterministic — every other consumer that listens for clicks on
 *     these buttons (form submit, native nav) still runs after the
 *     attribute-driven handler short-circuits.
 *
 * SSR / hydration:
 *   - The listener attaches in `useEffect`, so SSR renders a no-op
 *     host. The Vite SPA we ship is fully client-side, so this is
 *     belt-and-braces; harmless to leave in for a future SSR-enabled
 *     stack.
 *
 * Accessibility:
 *   - Each modal handles its own focus trap + ESC dismissal via the
 *     `ConfigEditorModal` shell (built on Radix Dialog). The host
 *     itself is non-visual; it only flips state.
 *
 * T-0116 misc-modal value conventions:
 *   - data-open-evidence carries `source_type:source_id` so the host
 *     can resolve which side of the graph to fetch. Optional segments
 *     can carry display id + title: `source_type:source_id:display_id`
 *     and `data-evidence-title="…"` on the trigger element.
 *   - data-create carries the bare kind ("risk", "rfi", …). The host
 *     resolves the engagement id from the URL; the framework_definition_id
 *     stays a host-resolution gap (flagged) — without it the form
 *     surfaces a validation banner instead of writing.
 *   - data-open-crit-history carries the readiness score row id (a
 *     register_entry uuid). The trigger may also carry
 *     `data-criterion-name` and `data-criterion-id` for the header.
 */

type ActiveModal =
  | { kind: 'rubric'; engagementId: string }
  | { kind: 'domains'; engagementId: string }
  | { kind: 'widgets'; frameworkDefinitionId: string }
  | { kind: 'parse'; documentId: string; engagementId?: string }
  | { kind: 'evidence'; engagementId: string; focal: EvidenceGraphFocal }
  | { kind: 'create'; engagementId: string; createKind: CreateKind }
  | {
      kind: 'crit-history';
      engagementId: string;
      scoreId: string;
      criterionId?: string;
      criterionName?: string;
    }
  | { kind: 'email-templates'; initialEventType?: string }
  | { kind: 'index'; engagementId: string; engagementName?: string }
  | { kind: 'engagement-wizard'; preselectClientId?: string }
  | {
      kind: 'override';
      engagementId: string;
      entryId: string;
      registerType: string;
      fieldId?: string;
    }
  | null;

function findTrigger(target: EventTarget | null): {
  attr: string;
  value: string;
  element: HTMLElement;
} | null {
  if (!(target instanceof Element)) return null;
  // Walk up from the click target looking for any recognized data-open-* attr.
  // Buttons may wrap an icon span, so `target` is rarely the button itself.
  const candidates = [
    'data-open-rubric',
    'data-open-domains',
    'data-open-widgets',
    'data-open-parse',
    'data-open-evidence',
    'data-create',
    'data-open-crit-history',
    'data-open-email-templates',
    'data-open-index',
    'data-open-override',
  ];
  let cur: Element | null = target;
  while (cur && cur !== document.body) {
    for (const attr of candidates) {
      if (cur.hasAttribute(attr)) {
        const value = cur.getAttribute(attr) ?? '';
        // The misc-modal attributes accept an empty value when the modal
        // has no required seed (data-open-email-templates,
        // data-open-index). The editor attributes still require a value.
        const allowEmpty = attr === 'data-open-email-templates' || attr === 'data-open-index';
        if (value.length === 0 && !allowEmpty) return null;
        return { attr, value, element: cur as HTMLElement };
      }
    }
    cur = cur.parentElement;
  }
  return null;
}

const CREATE_KINDS: ReadonlySet<CreateKind> = new Set<CreateKind>([
  'risk',
  'issue',
  'rfi',
  'finding',
  'interview',
]);

const EVIDENCE_SOURCE_TYPES: ReadonlySet<EvidenceSourceType> = new Set<EvidenceSourceType>([
  'register_entry',
  'rfi',
]);

function readData(el: HTMLElement, key: string): string | undefined {
  const v = el.getAttribute(key);
  return v && v.length > 0 ? v : undefined;
}

export function EditorModalHost() {
  const [active, setActive] = useState<ActiveModal>(null);

  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      // Ignore right-click / middle-click; they aren't activations.
      if (ev.button !== 0) return;
      const hit = findTrigger(ev.target);
      if (!hit) return;

      // Some buttons opt out by disabled=true; check before opening.
      if (hit.element instanceof HTMLButtonElement && hit.element.disabled) return;

      // For parse triggers, also propagate the closest engagement id from
      // the URL so the reprocess mutation can invalidate the right
      // engagement-scoped queries. The DOM doesn't carry it directly here;
      // we sniff `/engagements/:id/` from `location.pathname`.
      const engagementIdFromUrl = (() => {
        const m = window.location.pathname.match(/\/engagements\/([0-9a-f-]{36})/i);
        return m ? m[1] : undefined;
      })();

      switch (hit.attr) {
        case 'data-open-rubric':
          ev.preventDefault();
          setActive({ kind: 'rubric', engagementId: hit.value });
          break;
        case 'data-open-domains':
          ev.preventDefault();
          setActive({ kind: 'domains', engagementId: hit.value });
          break;
        case 'data-open-widgets':
          ev.preventDefault();
          setActive({ kind: 'widgets', frameworkDefinitionId: hit.value });
          break;
        case 'data-open-parse':
          ev.preventDefault();
          setActive({ kind: 'parse', documentId: hit.value, engagementId: engagementIdFromUrl });
          break;
        case 'data-open-evidence': {
          // Value shape: "source_type:source_id[:display_id]". Optional
          // `data-evidence-title` + `data-evidence-subtitle` on the trigger
          // element flesh out the focal node copy.
          const parts = hit.value.split(':');
          const sourceTypeRaw = parts[0];
          const sourceId = parts[1];
          if (!sourceTypeRaw || !sourceId) break;
          if (!EVIDENCE_SOURCE_TYPES.has(sourceTypeRaw as EvidenceSourceType)) break;
          if (!engagementIdFromUrl) break;
          ev.preventDefault();
          const focal: EvidenceGraphFocal = {
            source_type: sourceTypeRaw as EvidenceSourceType,
            source_id: sourceId,
            display_id: parts[2] ?? readData(hit.element, 'data-evidence-display-id') ?? sourceId,
            title: readData(hit.element, 'data-evidence-title') ?? sourceId,
            subtitle: readData(hit.element, 'data-evidence-subtitle'),
          };
          setActive({ kind: 'evidence', engagementId: engagementIdFromUrl, focal });
          break;
        }
        case 'data-create': {
          // T-0117 / C20-127 — `data-create="engagement"` opens the
          // new-engagement wizard. The wizard is the special
          // case here: it doesn't require an engagement_id from the
          // URL (it's the thing being created) and may carry an
          // optional `data-preselect-client-id` to deep-link the
          // client step. All other `data-create` values route into
          // the register CreateFormModal as before.
          if (hit.value === 'engagement') {
            ev.preventDefault();
            const preselectClientId =
              readData(hit.element, 'data-preselect-client-id') ??
              // Fallback: clients/$clientId page — sniff the id out of the URL
              // so a CTA on the client-detail page that omits the explicit
              // data attribute still opens with the client preselected.
              ((): string | undefined => {
                const m = window.location.pathname.match(/\/clients\/([0-9a-f-]{36})(?:\/|$)/i);
                return m ? m[1] : undefined;
              })();
            setActive({ kind: 'engagement-wizard', preselectClientId });
            break;
          }
          const createKind = hit.value as CreateKind;
          if (!CREATE_KINDS.has(createKind)) break;
          if (!engagementIdFromUrl) break;
          ev.preventDefault();
          setActive({ kind: 'create', engagementId: engagementIdFromUrl, createKind });
          break;
        }
        case 'data-open-crit-history': {
          if (!engagementIdFromUrl) break;
          ev.preventDefault();
          setActive({
            kind: 'crit-history',
            engagementId: engagementIdFromUrl,
            scoreId: hit.value,
            criterionId: readData(hit.element, 'data-criterion-id'),
            criterionName: readData(hit.element, 'data-criterion-name'),
          });
          break;
        }
        case 'data-open-email-templates':
          ev.preventDefault();
          setActive({
            kind: 'email-templates',
            initialEventType: hit.value.length > 0 ? hit.value : undefined,
          });
          break;
        case 'data-open-override': {
          // Value = register entry id. The register type rides on the
          // trigger itself or any ancestor (rows stamp it once on the
          // <tr> so per-cell triggers inherit via closest()). An optional
          // data-override-cell narrows the override to one field.
          if (!engagementIdFromUrl) break;
          const registerType =
            readData(hit.element, 'data-register-type') ??
            hit.element.closest('[data-register-type]')?.getAttribute('data-register-type') ??
            undefined;
          if (!registerType) break;
          ev.preventDefault();
          setActive({
            kind: 'override',
            engagementId: engagementIdFromUrl,
            entryId: hit.value,
            registerType,
            fieldId: readData(hit.element, 'data-override-cell'),
          });
          break;
        }
        case 'data-open-index': {
          if (!engagementIdFromUrl) break;
          ev.preventDefault();
          setActive({
            kind: 'index',
            engagementId: engagementIdFromUrl,
            engagementName: readData(hit.element, 'data-engagement-name'),
          });
          break;
        }
        default:
          break;
      }
    };

    // Capture phase so we short-circuit before route navigation or form
    // submission can fire. The modal triggers are not links, so this only
    // affects synthetic button clicks; no risk to navigation a11y.
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  if (!active) return null;

  if (active.kind === 'rubric') {
    return (
      <RubricEditorModal
        engagementId={active.engagementId}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'domains') {
    return (
      <DomainEditorModal
        engagementId={active.engagementId}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'widgets') {
    return (
      <WidgetEditorModal
        frameworkDefinitionId={active.frameworkDefinitionId}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'parse') {
    return (
      <ParseTreeModal
        documentId={active.documentId}
        engagementId={active.engagementId}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'evidence') {
    return (
      <EvidenceGraphModal
        engagementId={active.engagementId}
        focal={active.focal}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'create') {
    return (
      <CreateFormModal
        engagementId={active.engagementId}
        kind={active.createKind}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'crit-history') {
    return (
      <CriterionHistoryModal
        engagementId={active.engagementId}
        scoreId={active.scoreId}
        criterionId={active.criterionId}
        criterionName={active.criterionName}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'email-templates') {
    return (
      <EmailTemplateModal
        initialEventType={active.initialEventType}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'index') {
    return (
      <SearchIndexModal
        engagementId={active.engagementId}
        engagementName={active.engagementName}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  if (active.kind === 'override') {
    return (
      <OverrideHostBridge
        engagementId={active.engagementId}
        entryId={active.entryId}
        registerType={active.registerType}
        fieldId={active.fieldId}
        onClose={close}
      />
    );
  }
  if (active.kind === 'engagement-wizard') {
    return (
      <NewEngagementWizard
        preselectClientId={active.preselectClientId}
        open
        onOpenChange={(o) => {
          if (!o) close();
        }}
      />
    );
  }
  return null;
}
